(() => {
  if (window.__sportszoneQuickAddLoaded) return;
  window.__sportszoneQuickAddLoaded = true;

  const root = document.getElementById('SportsZoneQuickAdd');
  if (!root) return;

  const panel = root.querySelector('.szqa__panel');
  const loading = root.querySelector('[data-szqa-loading]');
  const productWrap = root.querySelector('[data-szqa-product]');
  const image = root.querySelector('[data-szqa-image]');
  const title = root.querySelector('[data-szqa-title]');
  const price = root.querySelector('[data-szqa-price]');
  const details = root.querySelector('[data-szqa-details]');
  const optionsWrap = root.querySelector('[data-szqa-options]');
  const errorBox = root.querySelector('[data-szqa-error]');
  const cache = new Map();

  const REQUIRED_CONFIGURATION_HANDLES = new Set([
    'bauer-vapor-flylite-senior-hockey-skates',
    'bauer-vapor-flylite-red-senior-hockey-skates',
    'bauer-vapor-flylite-intermediate-hockey-skates',
    'bauer-vapor-flypro-senior-hockey-skates',
    'bauer-vapor-flypro-intermediate-hockey-skates',
    'bauer-supreme-fuse-senior-skate',
    'bauer-supreme-fuse-intermediate-hockey-skates',
    'bauer-supreme-f50-pro-senior-hockey-skates',
    'bauer-supreme-f50-pro-intermediate-hockey-skates',
    'bauer-supreme-m50-pro-senior-hockey-skates',
    'bauer-supreme-m50-pro-intermediate-hockey-skates',
    '1059765',
    '1059766',
    '1061682',
    '1061683',
    '1061681',
    '1061726',
    '1062089',
    'bauer-vapor-xltx-pro-plus-intermediate-hockey-skates-2023',
    'bauer-supreme-shadow-senior-hockey-skates',
    'bauer-supreme-shadow-intermediate-hockey-skates'
  ]);

  function requiresConfiguration(product) {
    return REQUIRED_CONFIGURATION_HANDLES.has(String(product?.handle || ''));
  }

  function variantActionLabel(available) {
    if (!available) return 'Sold out';
    return requiresConfiguration(currentProduct) ? 'Continue setup' : 'Add to cart';
  }

  function continueSetup(variantId) {
    const url = new URL(currentProductUrl || '/', window.location.origin);
    if (variantId) url.searchParams.set('variant', String(variantId));
    closeQuickAdd({restoreFocus: false});
    window.location.href = url.toString();
  }

  function handleVariantAction(variant, button) {
    if (!variant?.id || !variant.available) return;
    if (requiresConfiguration(currentProduct)) {
      continueSetup(variant.id);
      return;
    }
    addVariant(variant.id, button);
  }

  let currentProduct = null;
  let currentProductUrl = '';
  let selections = {};
  let lastTrigger = null;
  let closingTimer = null;

  const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function currencyCode() {
    return window.Shopify?.currency?.active || 'CAD';
  }

  function formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode(),
        currencyDisplay: 'narrowSymbol'
      }).format(value);
    } catch (e) {
      return '$' + value.toFixed(2);
    }
  }

  function canonicalProductJsonUrl(url) {
    const parsed = new URL(url, window.location.origin);
    const index = parsed.pathname.indexOf('/products/');
    const productPath = index >= 0 ? parsed.pathname.slice(0, index) + parsed.pathname.slice(index) : parsed.pathname;
    return productPath.replace(/\.js$/, '').replace(/\/$/, '') + '.js';
  }

  function normalizeOptions(product) {
    const variants = product.variants || [];
    const raw = Array.isArray(product.options) ? product.options : [];
    return raw.map((option, index) => {
      const name = typeof option === 'string' ? option : (option?.name || ('Option ' + (index + 1)));
      const position = typeof option === 'object' && option?.position ? Number(option.position) : index + 1;
      let values = typeof option === 'object' && Array.isArray(option?.values) ? option.values.slice() : [];
      if (!values.length) {
        values = [...new Set(variants.map(variant => variant.options?.[position - 1] ?? variant['option' + position]).filter(Boolean))];
      }
      return { name, position, values };
    });
  }

  function variantValues(variant, count) {
    if (Array.isArray(variant.options)) return variant.options.slice(0, count);
    return Array.from({length: count}, (_, i) => variant['option' + (i + 1)]);
  }

  function productImage(product) {
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image?.src) return product.featured_image.src;
    if (Array.isArray(product.images) && product.images.length) {
      return typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.src;
    }
    return '';
  }

  function setError(message) {
    if (!message) {
      errorBox.hidden = true;
      errorBox.textContent = '';
      return;
    }
    errorBox.textContent = message;
    errorBox.hidden = false;
  }

  function setPrice(product, variant = null) {
    const active = variant || (product.variants || []).find(v => v.available) || (product.variants || [])[0];
    const activePrice = active?.price ?? product.price ?? 0;
    const compare = active?.compare_at_price ?? product.compare_at_price ?? 0;
    price.innerHTML = '';

    const current = document.createElement('span');
    current.textContent = formatMoney(activePrice);

    if (Number(compare) > Number(activePrice)) {
      current.className = 'szqa__sale';
      const old = document.createElement('s');
      old.textContent = formatMoney(compare);
      price.append(current, old);
    } else {
      price.append(current);
    }
  }

  function matchingVariants(product, nextSelections = selections) {
    const opts = normalizeOptions(product);
    return (product.variants || []).filter(variant => {
      const values = variantValues(variant, opts.length);
      return Object.entries(nextSelections).every(([key, selected]) => {
        if (!selected) return true;
        return values[Number(key)] === selected;
      });
    });
  }

  function availableFor(optionIndex, value) {
    const next = {...selections, [optionIndex]: value};
    return matchingVariants(currentProduct, next).some(variant => variant.available);
  }

  function clearSelectionsAfter(index) {
    Object.keys(selections).forEach(key => {
      if (Number(key) > index) delete selections[key];
    });
  }

  function autoSelectForced(options) {
    for (let i = 0; i < Math.max(0, options.length - 1); i += 1) {
      if (selections[i]) continue;
      const availableValues = options[i].values.filter(value => availableFor(i, value));
      if (availableValues.length === 1) {
        selections[i] = availableValues[0];
      } else {
        break;
      }
    }
  }

  function exactVariantFromValues(values) {
    const options = normalizeOptions(currentProduct);
    return (currentProduct.variants || []).find(variant => {
      const variantOptionValues = variantValues(variant, options.length);
      return values.every((value, index) => variantOptionValues[index] === value);
    });
  }

  function makeGroup(option, index) {
    const group = document.createElement('div');
    group.className = 'szqa__group';

    const heading = document.createElement('p');
    heading.className = 'szqa__group-title';
    heading.textContent = 'Select ' + option.name;
    group.appendChild(heading);

    const chips = document.createElement('div');
    chips.className = 'szqa__chips';

    option.values.forEach(value => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'szqa__option-chip';
      button.textContent = value;
      button.disabled = !availableFor(index, value);
      button.classList.toggle('is-selected', selections[index] === value);
      button.setAttribute('aria-pressed', selections[index] === value ? 'true' : 'false');

      button.addEventListener('click', () => {
        selections[index] = value;
        clearSelectionsAfter(index);
        renderOptions();
      });

      chips.appendChild(button);
    });

    group.appendChild(chips);
    return group;
  }

  function makeVariantRow(value, variant) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'szqa__variant-row';
    button.disabled = !variant?.available;
    if (variant?.id) button.dataset.variantId = variant.id;

    const valueLabel = document.createElement('span');
    valueLabel.className = 'szqa__variant-value';
    valueLabel.textContent = value;

    const action = document.createElement('span');
    action.className = 'szqa__variant-action';

    const actionText = document.createElement('span');
    actionText.textContent = variantActionLabel(Boolean(variant?.available));

    const plus = document.createElement('span');
    plus.className = 'szqa__plus';
    plus.setAttribute('aria-hidden', 'true');
    plus.textContent = '+';

    action.appendChild(actionText);
    if (variant?.available) action.appendChild(plus);
    button.append(valueLabel, action);

    if (variant?.available) {
      button.addEventListener('click', () => handleVariantAction(variant, button));
    }

    return button;
  }

  function makeDefaultAdd(variant) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'szqa__single-add';
    button.disabled = !variant?.available;
    button.textContent = variantActionLabel(Boolean(variant?.available));
    if (variant?.available) button.addEventListener('click', () => handleVariantAction(variant, button));
    return button;
  }

  function renderOptions() {
    if (!currentProduct) return;

    setError('');
    optionsWrap.innerHTML = '';
    const options = normalizeOptions(currentProduct);

    if (requiresConfiguration(currentProduct)) {
      const note = document.createElement('div');
      note.className = 'szqa__config-note';
      note.innerHTML = '<strong>One more setup step</strong><span>Choose your size and fit here, then finish the required skate configuration on the product page.</span>';
      optionsWrap.appendChild(note);
    }

    const isDefaultOnly =
      options.length === 0 ||
      (options.length === 1 && options[0].values.length === 1 && String(options[0].values[0]).toLowerCase() === 'default title');

    if (isDefaultOnly) {
      const variant = (currentProduct.variants || [])[0];
      setPrice(currentProduct, variant);
      optionsWrap.appendChild(makeDefaultAdd(variant));
      return;
    }

    if (options.length === 1) {
      const heading = document.createElement('p');
      heading.className = 'szqa__rows-heading';
      heading.textContent = 'Select ' + options[0].name;
      optionsWrap.appendChild(heading);

      options[0].values.forEach(value => {
        const variant = exactVariantFromValues([value]);
        optionsWrap.appendChild(makeVariantRow(value, variant));
      });
      return;
    }

    autoSelectForced(options);

    for (let i = 0; i < options.length - 1; i += 1) {
      optionsWrap.appendChild(makeGroup(options[i], i));
    }

    const lastIndex = options.length - 1;
    const last = options[lastIndex];
    const heading = document.createElement('p');
    heading.className = 'szqa__rows-heading';
    heading.textContent = 'Select ' + last.name;
    optionsWrap.appendChild(heading);

    const missingIndex = options.slice(0, lastIndex).findIndex((option, index) => !selections[index]);
    if (missingIndex >= 0) {
      const helper = document.createElement('p');
      helper.className = 'szqa__helper';
      helper.textContent = 'Choose ' + options[missingIndex].name + ' first to see available ' + last.name + ' options.';
      optionsWrap.appendChild(helper);
      return;
    }

    last.values.forEach(value => {
      const values = options.map((option, index) => index === lastIndex ? value : selections[index]);
      const variant = exactVariantFromValues(values);
      optionsWrap.appendChild(makeVariantRow(value, variant));
    });
  }

  async function fetchProduct(url) {
    const jsonUrl = canonicalProductJsonUrl(url);
    if (cache.has(jsonUrl)) return cache.get(jsonUrl);

    const promise = fetch(jsonUrl, {headers: {'Accept': 'application/json'}})
      .then(response => {
        if (!response.ok) throw new Error('Product options failed to load.');
        return response.json();
      })
      .catch(error => {
        cache.delete(jsonUrl);
        throw error;
      });

    cache.set(jsonUrl, promise);
    return promise;
  }

  function fillProduct(product, productUrl) {
    currentProduct = product;
    currentProductUrl = productUrl;
    selections = {};

    title.textContent = product.title || '';
    details.href = productUrl;
    details.setAttribute('aria-label', 'View full details for ' + (product.title || 'this product'));

    const src = productImage(product);
    if (src) {
      image.src = src;
      image.alt = product.title || '';
      image.parentElement.hidden = false;
    } else {
      image.removeAttribute('src');
      image.alt = '';
      image.parentElement.hidden = true;
    }

    setPrice(product);
    renderOptions();

    loading.hidden = true;
    productWrap.hidden = false;
  }

  async function openQuickAdd(trigger) {
    if (closingTimer) {
      clearTimeout(closingTimer);
      closingTimer = null;
    }

    lastTrigger = trigger;
    currentProduct = null;
    currentProductUrl = trigger.dataset.productUrl || '';
    selections = {};
    setError('');
    productWrap.hidden = true;
    loading.hidden = false;

    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('szqa-open');

    requestAnimationFrame(() => {
      root.classList.add('is-open');
      panel.focus({preventScroll: true});
    });

    const requestedProductUrl = currentProductUrl;
    try {
      const product = await fetchProduct(requestedProductUrl);
      if (!root.classList.contains('is-open') || currentProductUrl !== requestedProductUrl) return;
      fillProduct(product, requestedProductUrl);
    } catch (error) {
      if (!root.classList.contains('is-open') || currentProductUrl !== requestedProductUrl) return;
      loading.hidden = true;
      productWrap.hidden = false;
      title.textContent = 'Product options unavailable';
      price.innerHTML = '';
      optionsWrap.innerHTML = '';
      details.href = requestedProductUrl || '#';
      setError('Please open the product page to choose your options.');
    }
  }

  function closeQuickAdd({restoreFocus = true} = {}) {
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    if (lastTrigger) lastTrigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('szqa-open');

    closingTimer = window.setTimeout(() => {
      root.hidden = true;
      closingTimer = null;
    }, 300);

    if (restoreFocus && lastTrigger) {
      lastTrigger.focus({preventScroll: true});
    }
  }

  async function addVariant(variantId, button) {
    if (!variantId || button.dataset.loading === 'true') return;

    setError('');
    button.dataset.loading = 'true';
    button.disabled = true;

    const originalText = button.textContent;
    if (button.classList.contains('szqa__variant-row')) {
      const actionText = button.querySelector('.szqa__variant-action > span:first-child');
      if (actionText) actionText.textContent = 'Adding';
    } else {
      button.textContent = 'Adding to cart';
    }

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        formData.append('sections', cartDrawer.getSectionsToRender().map(section => section.id).join(','));
        formData.append('sections_url', window.location.pathname);
        if (lastTrigger && typeof cartDrawer.setActiveElement === 'function') {
          cartDrawer.setActiveElement(lastTrigger);
        }
      }

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'This option was not added. Please try again.');
      }

      closeQuickAdd({restoreFocus: false});

      if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
        window.setTimeout(() => cartDrawer.renderContents(data), 120);
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }
    } catch (error) {
      button.dataset.loading = 'false';
      button.disabled = false;
      if (button.classList.contains('szqa__variant-row')) {
        const actionText = button.querySelector('.szqa__variant-action > span:first-child');
        if (actionText) actionText.textContent = 'Add to cart';
      } else {
        button.textContent = originalText;
      }
      setError(error.message || 'This option was not added. Please try again.');
    }
  }

  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-sz-quick-add]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      openQuickAdd(trigger);
      return;
    }

    if (event.target.closest('[data-szqa-close]')) {
      event.preventDefault();
      closeQuickAdd();
    }
  });

  document.addEventListener('keydown', event => {
    if (!root.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeQuickAdd();
      return;
    }

    if (event.key === 'Tab') {
      const focusable = [...panel.querySelectorAll(focusableSelector)].filter(el => !el.disabled && el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });
})();